import { userRequest } from "./requestMethods";

/**
 * POST /api/quote and POST /api/quote/cart: the server's signed delivery
 * quote, shared by the checkout screens (through the deliveryFee slice) and
 * the carts' estimate (hooks/useDeliveryEstimate, hooks/useCartQuote).
 *
 * userRequest attaches the session token only when there is one, so a
 * signed-out shopper gets a guest quote (the routes are optionalAuth on the
 * server). The fee, distance, offer, goods subtotal, total and minimum order
 * are always the server's numbers: a request carries ids, quantities and
 * choices only, never a price.
 */

const optionIdsOf = (item) =>
  (Array.isArray(item?.options) ? item.options : [])
    .map((option) => option?.optionId || option?._id)
    .filter(Boolean)
    .map(String);

const choicesOf = (item) => {
  const specs = item?.selectedSpecs;
  if (!specs || typeof specs !== "object") return null;
  const picked = Object.entries(specs).filter(([, value]) => typeof value === "string" && value);
  return picked.length ? Object.fromEntries(picked) : null;
};

/* { productId, qty, options?, selectedSpecs? } lines, from any cart or
   checkout line shape. The options and choices let the server price a dish's
   extras and a brand size the way order create does. */
export const toQuoteItems = (items) =>
  (Array.isArray(items) ? items : [])
    .map((item) => {
      const line = {
        productId: item?.productId?._id || item?.productId || item?._id || item?.id,
        qty: item?.qty || item?.quantity || 1,
      };
      const options = optionIdsOf(item);
      if (options.length) line.options = options.map((id) => ({ _id: id }));
      const choices = choicesOf(item);
      if (choices) line.selectedSpecs = choices;
      return line;
    })
    .filter((line) => Boolean(line.productId));

/* A stable fingerprint of the lines, for memoising on what the server prices. */
export const quoteItemsKey = (items) =>
  toQuoteItems(items)
    .map((line) =>
      [
        line.productId,
        line.qty,
        (line.options || []).map((option) => option._id).join(","),
        line.selectedSpecs ? JSON.stringify(line.selectedSpecs) : "",
      ].join(":")
    )
    .join("|");

/* The drop-off, unless the quote is for the goods only (no area set yet):
   the server then prices the goods and the minimum and leaves the fee null. */
const dropoffOf = ({ customerLat, customerLng, goodsOnly }) =>
  goodsOnly ? {} : { dropoffCoords: { lat: customerLat, lng: customerLng } };

export const requestDeliveryQuote = async ({
  vendorId,
  outletId,
  customerLat,
  customerLng,
  items,
  mode,
  paymentMethod,
  goodsOnly = false,
}) => {
  const response = await userRequest.post("/api/quote", {
    vendorId,
    // /api/quote resolves the OUTLET's own coords + radius when it is given
    // an outletId, and silently falls back to the parent vendor when it is
    // not. Order-create already prices from the outlet, so omitting it here
    // would measure the quoted and the charged fee from two addresses.
    ...(outletId ? { outletId } : {}),
    ...dropoffOf({ customerLat, customerLng, goodsOnly }),
    ...(Array.isArray(items) ? { items } : {}),
    ...(mode ? { mode } : {}),
    // A checkout names its payment method, so the quote's total carries the
    // processing fee order create adds for it (0 while the pass-through is off).
    ...(typeof paymentMethod === "string" && paymentMethod ? { paymentMethod } : {}),
  });
  return response.data;
};

/**
 * A whole multi-vendor cart in one request. `vendors` are
 * { key, vendorId, outletId?, items } (items already in toQuoteItems shape).
 * The answer lists one quote per vendor, by key, each that vendor's own (its
 * full fee: a free delivery goes to the first cart checked out), and
 * `offerNote`: the server's one sentence about the shopper's free delivery.
 */
export const requestCartQuote = async ({ vendors, customerLat, customerLng, goodsOnly = false }) => {
  const response = await userRequest.post("/api/quote/cart", {
    ...dropoffOf({ customerLat, customerLng, goodsOnly }),
    vendors: (Array.isArray(vendors) ? vendors : []).map(({ key, vendorId, outletId, items }) => ({
      key,
      vendorId,
      ...(outletId ? { outletId } : {}),
      items: Array.isArray(items) ? items : [],
    })),
  });
  return response.data;
};
